// src/components/SceneLayers/Nodes/Node/IconTypes/IsometricIcon.tsx
// ✅ PRODUCTION: Clean version without debug markers

import { useRef, useEffect } from 'react';
import { PROJECTED_TILE_SIZE } from 'src/config';

interface Props {
  url: string;
  onImageLoaded?: () => void;
}

export const IsometricIcon = ({ url, onImageLoaded }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Track if component is still mounted
    let isMounted = true;
    const container = containerRef.current;

    const img = document.createElement('img');
    img.src = url;
    img.alt = 'isometric-icon';
    img.draggable = false;

    const iconWidth = PROJECTED_TILE_SIZE.width * 0.7;

    img.onload = () => {
      // Check if component is still mounted before accessing DOM
      if (!isMounted || !container) return;

      const aspectRatio = img.naturalWidth / img.naturalHeight || 1;
      const iconHeight = iconWidth / aspectRatio;

      // Final tuned adjustments (update these with your final values from testing)
      const HORIZONTAL_ADJUST = -5;  // Update after testing
      const VERTICAL_ADJUST = -15;     // Update after testing

      const leftOffset = -iconWidth / 2 + HORIZONTAL_ADJUST;
      const topOffset = -iconHeight / 2 - PROJECTED_TILE_SIZE.height / 2 + VERTICAL_ADJUST;

      img.style.cssText = `
        position: absolute;
        left: ${leftOffset}px;
        top: ${topOffset}px;
        width: ${iconWidth}px !important;
        height: ${iconHeight}px !important;
        max-width: ${iconWidth}px !important;
        max-height: ${iconHeight}px !important;
        object-fit: contain;
        display: block;
        pointer-events: auto;
        -webkit-user-drag: none;
        user-select: none;
      `;

      container.innerHTML = '';
      container.appendChild(img);

      onImageLoaded?.();
    };

    img.onerror = (e) => {
      console.error('❌ IsometricIcon failed to load:', url, e);
    };

    return () => {
      isMounted = false;
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [url, onImageLoaded]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        pointerEvents: 'auto'
      }}
    />
  );
};
